// Ordered chart / categorical series colors (Mantine color names).
//
// CVD-ordered: the first four — sage, coral, plum, gold — stay distinct under
// red-green and blue-yellow colorblindness. emerald and magenta collapse with
// sage/plum under CVD, so they sit last and should only be used as a 5th/6th
// series AND always with a non-color cue (legend, direct label, marker, dash).
// Charts must never rely on color alone.
//
// sage + plum are reused brand colors (non-semantic). The semantic colors
// (error/warning/success/info) are intentionally NOT here — a chart series must
// never be read as a status. For ordered/quantitative data use a single color
// scale ramp instead (e.g. one color's 0→9 shades), not this categorical set.
// See the "Categorical / chart palette" section on the /design page.
export const CHART_SEQUENCE = [
    'sage',
    'coral',
    'plum',
    'gold',
    'emerald',
    'magenta',
] as const

export type ChartColor = (typeof CHART_SEQUENCE)[number]

// Nth series color name, wrapping if there are more series than colors.
export const chartColor = (index: number): ChartColor => {
    const n = CHART_SEQUENCE.length
    return CHART_SEQUENCE[((index % n) + n) % n]
}

// Non-color cues for MULTI-SERIES LINE / AREA charts, ordered to match
// CHART_SEQUENCE. Color alone is never enough (colorblindness, b/w print,
// overlapping lines), so pair every series' color with a dash pattern AND a
// point marker. Bar charts can't dash — distinguish their series with direct
// labels or fill patterns instead.
export const SERIES_DASH: readonly number[][] = [
    [], // solid
    [6, 3], // dashed
    [2, 2], // dotted
    [8, 3, 2, 3], // dash-dot
    [10, 4], // long dash
    [1, 3], // fine dotted
]

// Chart.js pointStyle names, distinct shapes in CHART_SEQUENCE order.
export const SERIES_POINT_STYLE = [
    'circle',
    'rectRot', // diamond
    'triangle',
    'rect',
    'star',
    'cross',
] as const

// Everything the Nth line/area series needs in one call, so the non-color cue is
// never forgotten: the color NAME plus its dash + marker. Resolve `color` to a
// concrete value before handing it to Chart.js — it draws on <canvas>, where CSS
// `var(--mantine-color-…)` strings don't apply (read the computed CSS variable,
// which also keeps the chart scheme-aware).
export const chartSeriesStyle = (index: number) => {
    const n = CHART_SEQUENCE.length
    const i = ((index % n) + n) % n
    return {
        color: CHART_SEQUENCE[i],
        borderDash: SERIES_DASH[i],
        pointStyle: SERIES_POINT_STYLE[i],
    }
}

// ---- Scheme-aware chart palette --------------------------------------------
//
// Chart.js draws on <canvas>, where CSS `var(--mantine-color-…)` strings don't
// resolve, so charts need concrete color values. Dark mode brightens the sage so
// bars POP on the dark surface and flips the axes/grid to light; light keeps the
// original tuned sage. Components pick this with `chartPalette(useComputedColorScheme())`:
// use `bar` for a single series, `series[i]` for a comparison (e.g. seed vs
// expanded — `series[1]` is the brighter, more prominent one).
export interface ChartPalette {
    bar: { fill: string; border: string }
    series: { fill: string; border: string }[]
    tick: string
    grid: string
}

export const chartPalette = (scheme: 'light' | 'dark'): ChartPalette =>
    scheme === 'dark'
        ? {
              bar: { fill: 'rgba(82, 180, 172, 0.9)', border: '#79CAC2' }, // sage 4/3
              series: [
                  { fill: 'rgba(48, 154, 146, 0.5)', border: '#309A92' }, // sage 5 — secondary
                  { fill: 'rgba(121, 202, 194, 0.85)', border: '#A5DED8' }, // sage 3/2 — primary, bright
              ],
              tick: '#C9C8C3',
              grid: 'rgba(255, 255, 255, 0.08)',
          }
        : {
              bar: {
                  fill: 'rgba(48, 154, 146, 0.85)',
                  border: 'rgb(21, 129, 122)',
              }, // sage 5/6
              series: [
                  {
                      fill: 'rgba(121, 202, 194, 0.55)',
                      border: 'rgb(121, 202, 194)',
                  }, // sage 3 — secondary
                  {
                      fill: 'rgba(48, 154, 146, 0.85)',
                      border: 'rgb(21, 129, 122)',
                  }, // sage 5/6 — primary
              ],
              tick: '#46433C',
              grid: 'rgba(30, 28, 24, 0.1)',
          }

// Responsive chart grid: as many ~300px columns as fit, wrapping to more rows as
// the CONTAINER (not the viewport) narrows — right for charts in a modal or a
// page alike. No max; 1fr lets each grow to fill its track.
export const CHART_GRID_STYLE = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
    gap: 'var(--mantine-spacing-md)',
} as const
